import { get, set } from "idb-keyval";
import { hydrate } from "../store/defaults";
import type { TrainingDraftAnswers } from "../domain/trainingQuestions";
import type { ActiveSession, EatingAnswers, KayaState } from "../domain/types";
import type { AuthUser } from "../sync/api";

/** Same key the original single-file PWA used, so an existing install migrates silently. */
export const LEGACY_KEY = "kaya-v1";
export const IDB_KEY = "kaya-state";
export const META_KEY = "kaya-meta";

export interface LocalMeta {
  /** epoch ms of the last local mutation — the vector we sync on */
  updatedAt: number;
  /** server revision last successfully pulled/pushed */
  rev?: number;
  userId?: string;
  /**
   * The signed-in account, as the server last described it.
   *
   * Cached because the app is now behind a sign-in and is still offline-first: on a launch with
   * no network the token alone cannot be exchanged for an identity, and without one the gate
   * would show the sign-in screen to somebody who is already signed in and cannot do anything
   * about it. The server's answer replaces this the moment one arrives.
   */
  user?: AuthUser;
  /**
   * The session open on this device, if any.
   *
   * Device-local on purpose: it is this phone that is walking, and a second phone showing
   * "Resume walk" for it would be a puzzle. Keeping it out of the synced blob is also what stops
   * the merge from erasing it. (ADR-027)
   */
  session?: ActiveSession | null;
  /**
   * When the first-run checklist on Today was put away, epoch ms.
   *
   * Device-local for the same reason as `session`: it records what this screen showed, not a
   * fact about the person, and a field in the synced blob would need a merge rule of its own.
   */
  setupDismissedAt?: number;
  /**
   * The treadmill question on the checklist was answered on this device — by the sheet's Yes/No
   * or a save from Treadmill settings. A stored `hasTreadmill: true` cannot say so, because it is
   * the default. Device-local for the same reason as `setupDismissedAt`.
   */
  setupTreadmillAnswered?: boolean;
  /**
   * Text size on this phone (kit §40). Device-local rather than a profile field: a parent reading
   * at Larger and the owner at Normal may share one account, and the right size is the eyes in
   * front of this screen, not the person signed in. It is kept across a sign-out for the same
   * reason. Absent is Normal.
   */
  textSize?: TextSize;
  /**
   * The questionnaire's optional last question — out of control around food, or making up for
   * eating. Only here, never in the synced blob: an admin can read an account (ADR-020), and this
   * is not a figure like weight but something a person tells one screen. "yes" switches the
   * deficit off on this phone (`domain/eating.ts::DeviceAnswers`). Cleared at sign-out, like
   * everything else that belongs to the person rather than the phone. (ADR-042)
   */
  eatingSafety?: EatingSafety;
  /**
   * Answers given so far in an unfinished questionnaire, so closing it midway keeps them ("Answers
   * so far are kept"). Nothing here counts until the read-back's Save writes `profile.eating`.
   */
  eatingDraft?: EatingDraft;
  /**
   * The exercise safety screens' answer (plan 2026-10-05 §3.5): "doctor" holds the plan at easy
   * walks and the gentle sessions. Only here, for `eatingSafety`'s reasons — an admin can read an
   * account, and a son answering "no chest pain" for his father is the wrong person answering.
   * Cleared at sign-out. (ADR-050)
   */
  trainingSafety?: TrainingSafety;
  /**
   * "Plan my exercise" left midway, as `eatingDraft` is for food. Nothing here counts until the
   * read-back saves `profile.training`. (ADR-053)
   */
  trainingDraft?: TrainingDraft;
  /**
   * The player's Simple mode, voice and language on this phone, beside `textSize` and for its
   * reason: the right player is the person in front of this screen. A linked owner's
   * `profile.training.simple` is only a suggestion; this wins. Kept across a sign-out. (ADR-050)
   */
  simplePlayer?: boolean;
  voice?: boolean;
  lang?: "en" | "hi";
  /** the day Train's "first one back" notice was put away with "My usual" (ADR-065) */
  breakNoticeOff?: string;
  /** The walk's planned length last chosen on this phone, minutes; null = open-ended (ADR-063). */
  walkPlanMin?: number | null;
  /** `startedAt`s of walks the counter noticed that the person said were not walks, newest last,
   *  capped at 50 (ADR-063). The phone's, like the offer: a "No" here is not a fact to sync. */
  noticedOff?: string[];
}

export type TextSize = "normal" | "large" | "larger";
export type EatingSafety = "no" | "yes" | "unsure";

export interface TrainingSafety {
  gate: "ok" | "doctor";
  /** "none", or what happened in the last six months, comma-joined keys */
  recent: "none" | string;
  /** the four "Before you start" answers, kept so the screen re-opens on them (ADR-053) */
  before?: SafetyBefore;
  at: number;
}

export interface SafetyBefore {
  chest?: boolean;
  faint?: boolean;
  breath?: boolean;
  supervised?: boolean;
}

export interface TrainingDraft {
  answers: TrainingDraftAnswers;
  /** the shared health answers when Plan my food has not been answered, saved to profile.eating */
  health?: EatingAnswers;
  before?: SafetyBefore;
  /** the recent-events keys picked; [] is "None" */
  recent?: string[];
  /** `profile.training.at` when the draft began: a save since makes it stale */
  since?: number;
  step: string;
}

export type PlayerPrefs = Partial<Pick<LocalMeta, "simplePlayer" | "voice" | "lang">>;

export interface EatingDraft {
  answers: EatingAnswers;
  safety?: EatingSafety;
  /** `profile.eating.at` when the draft began: a save since, from any phone, makes it stale */
  since?: number;
  /** the screen it was left on */
  step: string;
}

/** IndexedDB is the store of record; localStorage is kept as a mirror because the original
 *  PWA wrote there and because it is the only thing that survives an IDB eviction on iOS. */
export async function loadState(): Promise<KayaState> {
  try {
    const fromIdb = await get<KayaState>(IDB_KEY);
    if (fromIdb) return hydrate(fromIdb);
  } catch {
    /* private mode, blocked storage — fall through to localStorage */
  }
  try {
    const raw = localStorage.getItem(LEGACY_KEY) || localStorage.getItem("fl-tracker-v1");
    if (raw) return hydrate(JSON.parse(raw));
  } catch {
    /* ignore */
  }
  return hydrate(null);
}

export async function saveState(s: KayaState): Promise<void> {
  try {
    await set(IDB_KEY, s);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(LEGACY_KEY, JSON.stringify(s));
  } catch {
    /* quota or blocked — IDB copy still stands */
  }
}

export async function loadMeta(): Promise<LocalMeta> {
  try {
    return (await get<LocalMeta>(META_KEY)) || { updatedAt: 0 };
  } catch {
    return { updatedAt: 0 };
  }
}

export async function saveMeta(m: LocalMeta): Promise<void> {
  try {
    await set(META_KEY, m);
  } catch {
    /* ignore */
  }
}
